import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";

export const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const oauthMessage = searchParams.get("oauth_message");
  const businessId = searchParams.get("business_id");
  const redirectTo = searchParams.get("redirect_to");

  useEffect(() => {
    if (oauthMessage) {
      if (oauthMessage.includes("Successfully linked")) {
        setSuccess(true);
        toast.success("Google Business account connected successfully.");

        const timeout = setTimeout(() => {
          if (redirectTo === "post-onboarding") {
            void navigate("/waiting?google_business_modal=open", {
              replace: true,
            });
          } else if (businessId) {
            void navigate(`/business-settings/${businessId}`, {
              replace: true,
            });
          } else {
            void navigate("/dashboard", { replace: true });
          }
        }, 2000);

        return () => clearTimeout(timeout);
      } else {
        setError(oauthMessage);
        setIsProcessing(false);
      }
    } else {
      setError("No OAuth message received from server");
      setIsProcessing(false);
    }
  }, [oauthMessage, businessId, redirectTo, navigate]);

  if (isProcessing && !success) {
    return (
      <Stack
        gap={4}
        align="center"
        justify="center"
        className="h-full w-full p-4"
      >
        <Stack gap={4} align="center">
          <Spinner
            layout="centered"
            size="lg"
            aria-label="Connecting your Google Business account"
          />
          <Text variant="title" className="text-center">
            Connecting your Google Business account...
          </Text>
          <Text className="text-center text-subtle">
            Please wait while we complete the setup.
          </Text>
        </Stack>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack
        gap={4}
        align="center"
        justify="center"
        className="h-full w-full p-4"
      >
        <Stack gap={4} align="center" className="max-w-md">
          <Text variant="title" className="text-center text-danger">
            Connection Failed
          </Text>
          <Text className="text-center text-subtle">{error}</Text>
          <Stack align="start" direction="row" gap={2}>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Go Back
            </Button>
            {redirectTo === "post-onboarding" ? (
              <Button
                onClick={() => navigate("/waiting?google_business_modal=open")}
              >
                Try Again
              </Button>
            ) : businessId ? (
              <Button
                onClick={() => navigate(`/business-settings/${businessId}`)}
              >
                View Business Settings
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  if (success) {
    return (
      <Stack
        gap={4}
        align="center"
        justify="center"
        className="h-full w-full p-4"
      >
        <Stack gap={4} align="center">
          <Text variant="title" className="text-center text-success">
            Success!
          </Text>
          <Text className="text-center text-subtle">
            Your Google Business account has been connected successfully.
          </Text>
          <Text variant="caption" className="text-center text-subtle">
            Redirecting to business settings...
          </Text>
        </Stack>
      </Stack>
    );
  }

  return null;
};
